/**
 * Einstellungen → Betrieb → Begrüßungsvideo (Buchungsstrecke B4, sql/603).
 *
 * EIN festes Video je Betrieb (Idee Antoine, 29.09.2026: „Domenico bedankt sich für die Anfrage,
 * erzählt kurz über Restaurant und sich, dazu Bilder, ca. 45 Sek.“). Drei Teile, jeder für sich
 * hochgeladen — Video (MP4/WebM, höchstens 60 MB), Vorschaubild (Pflicht, sonst erscheint das Video
 * nirgends) und Untertitel (WebVTT, empfohlen: das Video startet beim Gast stumm). Alles liegt im
 * eigenen Speicher; kein YouTube/Vimeo.
 *
 * Hochladen geht als rohe Datei (POST /api/settings/begruessungsvideo/:teil, Typ im Content-Type) —
 * ein Video als Base64-Text wäre zu groß für den Server. „Video löschen“ entfernt alle drei Teile.
 */
import { useRef, useState } from "react";
import { Button, Icon, useBestaetigung } from "./components";
import { apiFetch, toApiError } from "../lib/api";
import { apiMutate } from "../hooks/useApi";

export const MAX_VIDEO_MB = 60;
const VIDEO_TYPEN = ["video/mp4", "video/webm"];
const BILD_TYPEN = ["image/jpeg", "image/png", "image/webp"];

type Teil = "video" | "vorschau" | "untertitel";

/** Welcher Typ an den Server geht — `null`, wenn die Datei nicht passt (Meldung für den Betrieb). */
export function uploadTyp(teil: Teil, datei: { name: string; type: string; size: number }): { typ: string } | { fehler: string } {
  if (teil === "video") {
    if (!VIDEO_TYPEN.includes(datei.type)) return { fehler: "Bitte ein Video als MP4 oder WebM." };
    if (datei.size > MAX_VIDEO_MB * 1024 * 1024) return { fehler: `Das Video ist zu groß (höchstens ${MAX_VIDEO_MB} MB). Bitte kürzer oder kleiner exportieren.` };
    return { typ: datei.type };
  }
  if (teil === "vorschau") {
    if (!BILD_TYPEN.includes(datei.type)) return { fehler: "Bitte ein Bild als JPG, PNG oder WebP." };
    if (datei.size > 8 * 1024 * 1024) return { fehler: "Das Bild ist zu groß (höchstens 8 MB)." };
    return { typ: datei.type };
  }
  // Browser melden für .vtt oft keinen Typ — der Server prüft den Inhalt („WEBVTT“ in der ersten Zeile).
  if (!/\.vtt$/i.test(datei.name) && datei.type !== "text/vtt") return { fehler: "Bitte Untertitel als WebVTT-Datei (.vtt)." };
  if (datei.size > 256 * 1024) return { fehler: "Die Untertitel-Datei ist zu groß (höchstens 256 KB)." };
  return { typ: "text/vtt" };
}

/** Datei-Knopf: verstecktes Datei-Feld, ausgelöst über die Button-Komponente. */
function DateiKnopf({
  teil,
  accept,
  vorhanden,
  label,
  busy,
  onDatei,
}: {
  teil: Teil;
  accept: string;
  vorhanden: boolean;
  label: string;
  busy: Teil | "loeschen" | null;
  onDatei: (teil: Teil, datei: File | undefined) => void;
}) {
  const eingabe = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={eingabe}
        type="file"
        accept={accept}
        className="hidden"
        disabled={busy !== null}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          onDatei(teil, file);
        }}
        data-testid={`begruessungsvideo-input-${teil}`}
      />
      <Button type="button" variant="secondary" className="!py-1.5" disabled={busy !== null} onClick={() => eingabe.current?.click()} data-testid={`begruessungsvideo-knopf-${teil}`}>
        {busy === teil ? "Lädt …" : vorhanden ? `${label} ersetzen` : `${label} hochladen`}
      </Button>
    </>
  );
}

export function BegruessungsvideoEinstellung({
  videoUrl,
  vorschauUrl,
  untertitelUrl,
  onChanged,
}: {
  videoUrl: string | null | undefined;
  vorschauUrl: string | null | undefined;
  untertitelUrl: string | null | undefined;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<Teil | "loeschen" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { bestaetigen, dialog } = useBestaetigung();

  async function hochladen(teil: Teil, datei: File | undefined) {
    if (!datei || busy) return;
    const pruefung = uploadTyp(teil, datei);
    if ("fehler" in pruefung) {
      setError(pruefung.fehler);
      return;
    }
    setBusy(teil);
    setError(null);
    try {
      const res = await apiFetch(`/api/settings/begruessungsvideo/${teil}`, { method: "POST", headers: { "Content-Type": pruefung.typ }, body: datei });
      if (!res.ok) throw await toApiError(res);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function loeschen() {
    const ok = await bestaetigen({
      titel: "Begrüßungsvideo löschen?",
      text: "Video, Vorschaubild und Untertitel werden entfernt. Gäste sehen danach nirgends mehr ein Video.",
      bestaetigen: "Video löschen",
      gefahr: true,
    });
    if (!ok) return;
    setBusy("loeschen");
    setError(null);
    try {
      await apiMutate("/api/settings/begruessungsvideo", "DELETE");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const sichtbar = Boolean(videoUrl && vorschauUrl);
  const irgendwas = Boolean(videoUrl || vorschauUrl || untertitelUrl);

  return (
    <div className="space-y-4" data-testid="begruessungsvideo-einstellung">
      {dialog}
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Ein kurzes Video (etwa 45 Sekunden), in dem sich dein Ansprechpartner für die Anfrage bedankt und euer Haus vorstellt. Gäste
        sehen das Vorschaubild in der Bestätigung nach der Anfrage (Formular und E-Mail) und das Video auf der Angebotsseite neben der
        persönlichen Nachricht. Es spielt erst auf Klick, startet stumm mit Untertiteln und liegt in deinem eigenen Speicher — ohne
        YouTube, ohne Tracking.
      </p>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex aspect-video w-full shrink-0 items-center justify-center overflow-hidden rounded-lg border border-outline-variant/50 bg-surface-container-low sm:w-64 dark:border-outline/30">
          {videoUrl ? (
            <video
              className="h-full w-full bg-black object-contain"
              src={videoUrl}
              poster={vorschauUrl ?? undefined}
              preload="none"
              controls
              muted
              playsInline
              data-testid="begruessungsvideo-vorschau"
            >
              {untertitelUrl && <track kind="captions" src={untertitelUrl} srcLang="de" label="Deutsch" default />}
            </video>
          ) : vorschauUrl ? (
            <img src={vorschauUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icon name="movie" className="text-secondary dark:text-secondary-fixed-dim" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap gap-2">
            <DateiKnopf teil="video" accept="video/mp4,video/webm" vorhanden={Boolean(videoUrl)} label="Video" busy={busy} onDatei={(t, d) => void hochladen(t, d)} />
            <DateiKnopf teil="vorschau" accept="image/jpeg,image/png,image/webp" vorhanden={Boolean(vorschauUrl)} label="Vorschaubild" busy={busy} onDatei={(t, d) => void hochladen(t, d)} />
            <DateiKnopf teil="untertitel" accept=".vtt,text/vtt" vorhanden={Boolean(untertitelUrl)} label="Untertitel" busy={busy} onDatei={(t, d) => void hochladen(t, d)} />
          </div>
          <ul className="space-y-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
            <li className="flex items-center gap-1.5">
              <Icon name={videoUrl ? "check_circle" : "radio_button_unchecked"} className="text-[16px]" /> Video (MP4 oder WebM, höchstens {MAX_VIDEO_MB} MB)
            </li>
            <li className="flex items-center gap-1.5">
              <Icon name={vorschauUrl ? "check_circle" : "radio_button_unchecked"} className="text-[16px]" /> Vorschaubild (Pflicht, JPG/PNG/WebP, am besten 16:9)
            </li>
            <li className="flex items-center gap-1.5">
              <Icon name={untertitelUrl ? "check_circle" : "radio_button_unchecked"} className="text-[16px]" /> Untertitel (WebVTT, .vtt — empfohlen, das Video startet stumm)
            </li>
          </ul>
          <p className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" data-testid="begruessungsvideo-status">
            {sichtbar ? "Gäste sehen das Video." : irgendwas ? "Noch nicht sichtbar: Video und Vorschaubild fehlen noch beide oder eines davon." : "Kein Video hinterlegt — Gäste sehen nichts davon."}
          </p>
          {error && <p className="font-body-sm text-[12px] text-error">{error}</p>}
          {irgendwas && (
            <Button type="button" variant="danger" className="!py-1.5" disabled={busy !== null} onClick={() => void loeschen()} data-testid="begruessungsvideo-loeschen">
              {busy === "loeschen" ? "Löscht …" : "Video löschen"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
